import type { ReactNode } from 'react';
import { OptionVerticalIcon } from '@/icons';
import { Button, Dropdown } from '@/ui_v3';
export interface KebabMenuItem {
  icon: ReactNode;
  label: string;
  onSelect: () => void;
  disabled?: boolean;
}

/**
 * ⋮ 버튼 하나로 여는 짧은 메뉴. 폴더 카드(수정·삭제)와 폴더 수정 행(이동·삭제)이 쓴다.
 *
 * - `Dropdown.Portal` 을 명시한다. 트리거가 `overflow` 를 가진 조상 안에 있어 빼먹으면 잘린다.
 * - Content 폭이 트리거 폭에 묶여 있어 `w-auto min-w-[8rem]` 로 되돌린다.
 * - 항목 콜백은 Radix 표준 `onSelect` 다 — 클릭·Enter·Space 를 한 곳에서 받는다.
 * - 트리거가 링크 카드 안에 있으면 `stopPropagation` 으로 카드 이동을 막는다 — 항목 클릭의 포털 버블링까지.
 */
export const KebabMenu = ({
  items,
  label,
  disabled,
  stopPropagation = false
}: {
  items: KebabMenuItem[];
  /** 아이콘만 있는 트리거의 접근성 이름. */
  label: string;
  disabled?: boolean;
  stopPropagation?: boolean;
}) => {
  /* 트리거는 링크 카드 안에 있을 수 있어 전파와 기본 동작(이동)을 함께 막는다. */
  const stopTrigger = (event: React.MouseEvent) => {
    if (!stopPropagation) return;
    event.stopPropagation();
    event.preventDefault();
  };
  /*
    항목은 포털에 그려져 DOM 상으로는 링크 밖이지만, React 이벤트는 포털을 넘어 링크의 onClick 까지 버블링한다.
    Radix `onSelect` 의 커스텀 이벤트를 막아도 이 클릭은 못 막으므로 Content 에서 클릭 자체를 끊는다.
  */
  const stopBubble = (event: React.MouseEvent) => {
    if (stopPropagation) event.stopPropagation();
  };
  return <Dropdown.Root data-sentry-element="unknown" data-sentry-component="KebabMenu" data-sentry-source-file="KebabMenu.tsx">
      <Dropdown.Trigger asChild data-sentry-element="unknown" data-sentry-source-file="KebabMenu.tsx">
        <Button variant={'transparent'} intent={'neutral'} size={'xs'} aria-label={label} disabled={disabled} onClick={stopTrigger} data-sentry-element="Button" data-sentry-source-file="KebabMenu.tsx">
          <OptionVerticalIcon size={'xs'} data-sentry-element="OptionVerticalIcon" data-sentry-source-file="KebabMenu.tsx" />
        </Button>
      </Dropdown.Trigger>
      <Dropdown.Portal data-sentry-element="unknown" data-sentry-source-file="KebabMenu.tsx">
        <Dropdown.Content align={'end'} className={'w-auto min-w-[8rem]'} onClick={stopBubble} data-sentry-element="unknown" data-sentry-source-file="KebabMenu.tsx">
          {items.map(item => <Dropdown.Item key={item.label} disabled={item.disabled} onSelect={item.onSelect}>
              {item.icon}
              {item.label}
            </Dropdown.Item>)}
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>;
};